import React from 'react';
import styled from 'styled-components';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  DropdownArrowIcon,
  DropdownCloseIcon,
  NextArrowIcon,
  PrevArrowIcon,
} from 'app/assets/icons';
import { convertDateToDDMMYYYY } from 'utils/common';
import { messages } from './messages';
import { useTranslation } from 'react-i18next';

type Props = {
  label?: string;
  handleClickApply: (e: any) => void;
  handleClickCancel: () => void;
  startDate: string | undefined;
  startDateDefault: string | undefined;
  setStartDateDefault: React.Dispatch<React.SetStateAction<string | undefined>>;
  endtDate: string | undefined;
  endtDateDefault: string | undefined;
  setEndtDateDefault: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedRangeValue: any;
  selectedRangeValueDefault: any;
  setSelectedRangeValueDefault: React.Dispatch<any>;
  isSelectedMonth?: boolean;
  setSelectedRangeValue: React.Dispatch<any>;
  onClearClick?: React.MouseEventHandler<SVGSVGElement>;
  cancelLabel?: string;
  confirmLabel?: string;
  setIsCustomDate?: React.Dispatch<React.SetStateAction<boolean>>;
  isCalenderVisible: boolean;
  setIsCalenderVisible: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
  hoverLabel?: string;
};

const CustomDateRangePicker = ({
  handleClickApply,
  handleClickCancel,
  startDate,
  endtDate,
  startDateDefault,
  setStartDateDefault,
  endtDateDefault,
  setEndtDateDefault,
  selectedRangeValue,
  selectedRangeValueDefault,
  setSelectedRangeValueDefault,
  isSelectedMonth,
  label,
  onClearClick,
  cancelLabel,
  confirmLabel,
  setIsCustomDate,
  setSelectedRangeValue,
  isCalenderVisible,
  setIsCalenderVisible,
  className,
  hoverLabel,
}: Props) => {
  //use translation
  const { t } = useTranslation();

  //useRef
  const dateRangeRef = React.useRef<HTMLDivElement>(null);
  const [isDesabled, setIsDesabled] = React.useState<boolean>(false);
  const [isHovered, setIsHovered] = React.useState<boolean>(false);
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  };
  const [isSmallScreen, setIsSmallScreen] = React.useState(false);

  const dateRangeFilterhandler = e => {
    const startDate = e[0];
    const endDate = e[1];
    const formattedStartDate = formatDate(startDate);
    const formattedEndtDate = formatDate(endDate);
    setStartDateDefault(formattedStartDate);
    setEndtDateDefault(formattedEndtDate);
    setSelectedRangeValueDefault(e);
  };

  const tileClassName = ({ date, view }) => {
    const classes = ['react-calendar__tile'];
    if (view === 'month' && date.getDay() === 0) {
      classes.push('sunday');
    } else if (view === 'month' && date.getDay() === 1) {
      classes.push('monday');
    }
    return classes.join(' ');
  };

  const handleClickCustom = (e: React.MouseEvent) => {
    setIsCalenderVisible(prev => !prev);
  };

  const handleMouseEnterEvent = () => {
    setIsHovered(checkValues ? true : false);
  };
  const handleMouseLeaveEvent = () => {
    setIsHovered(false);
  };

  const checkValues = selectedRangeValueDefault?.length
    ? selectedRangeValueDefault !== undefined
      ? convertDateToDDMMYYYY(selectedRangeValueDefault[0]) +
        '-' +
        convertDateToDDMMYYYY(
          selectedRangeValueDefault[1]?.toLocaleDateString(),
        )
      : selectedRangeValue !== undefined &&
        convertDateToDDMMYYYY(selectedRangeValue[0]) +
          '-' +
          convertDateToDDMMYYYY(selectedRangeValue[1]?.toLocaleDateString())
    : undefined;

  const buttonContent =
    isHovered && (selectedRangeValueDefault || selectedRangeValue)
      ? hoverLabel
      : !isHovered && checkValues
      ? checkValues
      : label;

  //set Weekday format on samll devices & large screen
  const formatWeekday = (locale: string | undefined, date: Date) => {
    const weekday = date.toLocaleDateString(locale || 'en-US', {
      weekday: 'short',
    });
    return isSmallScreen ? weekday.charAt(0) : weekday; // Show first letter on small screens
  };
  //useEffect for checking Resize for Weekday format
  React.useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 767.99);
    };
    handleResize(); // Check initial screen size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Effect to add click event listener to document when daterange picker is shown
  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isClickedOnCalendar =
        target.classList.contains('react-calendar') ||
        target.closest('.react-calendar');
      const isClickedOnButton = target.closest('.custom-button');

      // Only close if click is outside calendar AND outside button
      if (
        dateRangeRef.current &&
        !dateRangeRef.current.contains(target) &&
        !isClickedOnCalendar &&
        !isClickedOnButton
      ) {
        setIsCalenderVisible(false);

        if (!startDate && !endtDate) {
          setSelectedRangeValueDefault([]);
          setSelectedRangeValue([]);
          setStartDateDefault('');
          setEndtDateDefault('');
          if (isSelectedMonth) {
            setIsCustomDate?.(false);
            setSelectedRangeValue([]);
          }
        } else {
          const startDate = selectedRangeValue?.[0];
          const endDate = selectedRangeValue?.[1];
          const formattedStartDate = formatDate(startDate);
          const formattedEndtDate = formatDate(endDate);
          setStartDateDefault(formattedStartDate);
          setEndtDateDefault(formattedEndtDate);
          setSelectedRangeValueDefault(selectedRangeValue);
        }
      }
    };
    if (isCalenderVisible) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isCalenderVisible]);

  React.useEffect(() => {
    if (startDateDefault || endtDateDefault) {
      setIsDesabled(false);
    } else {
      setIsDesabled(true);
    }
  }, [startDateDefault, endtDateDefault]);

  return (
    <CommonWrapper className={`${className} custom-calendar-wrapper-main`}>
      <CustomButton
        className={`${!isSelectedMonth ? 'active' : 'inactive'} ${
          Boolean(isCalenderVisible) ? 'opened' : ''
        } custom-button ${checkValues ? 'selected' : ''}`}
        onClick={handleClickCustom}
        onMouseOver={handleMouseEnterEvent}
        onMouseOut={handleMouseLeaveEvent}
      >
        <div className="icon-wrapper ">
          <div className="btn-content"> {buttonContent}</div>
          {checkValues ? (
            <DropdownCloseIcon
              onClick={e => {
                e.stopPropagation();
                onClearClick?.(e);
              }}
            />
          ) : (
            <DropdownArrowIcon />
          )}
        </div>
      </CustomButton>
      <DateRangeWrapper ref={dateRangeRef} className="DateRangeWrapper">
        {Boolean(isCalenderVisible) && (
          <CalendarWrapper
            className={`calendar-inner-wrapper ${
              Boolean(isCalenderVisible) ? 'calendar-open' : ''
            }`}
          >
            {(endtDate || endtDateDefault) && (
              <div className="date-range-value">
                <div className="start-date-value date-value-button">
                  {startDateDefault ? startDateDefault : startDate}
                </div>
                <div className="in-between"> - </div>
                <div className="end-date-value date-value-button">
                  {endtDateDefault ? endtDateDefault : endtDate}
                </div>
              </div>
            )}
            <Calendar
              selectRange
              goToRangeStartOnSelect
              onChange={e => dateRangeFilterhandler(e)}
              formatShortWeekday={formatWeekday}
              className={`${
                endtDate || endtDateDefault ? '' : 'no-date-selected'
              } date-range-picker`}
              tileClassName={tileClassName}
              value={
                selectedRangeValueDefault
                  ? selectedRangeValueDefault
                  : selectedRangeValue
              }
              nextLabel={
                <>
                  <NextArrowIcon className="btn medium-sz dark next-btn" />
                </>
              }
              prevLabel={
                <>
                  <PrevArrowIcon className="btn medium-sz dark next-btn" />
                </>
              }
            />
            <Footer className="Footer">
              <button
                button-Type="secondary"
                className="cancel-button footer-buttons"
                onClick={handleClickCancel}
              >
                {cancelLabel ?? t(messages.cancel())}
              </button>
              <button
                disabled={isDesabled}
                button-Type="secondary"
                className={`${
                  isDesabled ? 'disabled' : ''
                } apply-button footer-buttons`}
                onClick={() =>
                  handleClickApply(
                    selectedRangeValueDefault
                      ? selectedRangeValueDefault
                      : selectedRangeValue,
                  )
                }
              >
                {confirmLabel ?? t(messages.Apply())}
              </button>
            </Footer>
          </CalendarWrapper>
        )}
      </DateRangeWrapper>
    </CommonWrapper>
  );
};

export default CustomDateRangePicker;
const CommonWrapper = styled.div`
  width: fit-content;
  @media (max-width: 767.98px) {
    .calendar-inner-wrapper {
      position: fixed;
      top: 50%;
      left: 50%;

      transform: translateX(-50%) translateY(100%);
      opacity: 0;
      transition: opacity 0.5s ease, transform 0.5s ease; /* Smooth transition */
      &.calendar-open {
        opacity: 1;
        transform: translate(-50%, -50%); /* Center it */
        width: 305px;
        border-radius: 8px;
        .react-calendar {
          border-top-right-radius: 8px;
          border-top-left-radius: 8px;
        }
        .react-calendar__month-view {
          .react-calendar__tile {
            font-family: 'Open Sans';
            font-size: 13px;
            font-weight: 400;
            line-height: 19.5px;
          }
        }
      }
      .react-calendar__navigation__label {
        font-size: 16px !important;
        font-weight: 600 !important;
      }
      .date-range-value {
        .date-value-button {
          font-family: 'Open Sans';
          font-size: 13px;
          font-weight: 600;
          line-height: 19.5px;
        }
      }
      .Footer {
        border-bottom-right-radius: 8px;
        border-bottom-left-radius: 8px;
        .footer-buttons {
          border-radius: 8px;
        }
      }
    }
  }

  @media (max-width: 1536px) {
    .calendar-open {
      opacity: 1;
    }
    .calendar-inner-wrapper {
      width: 280px;
    }
    .date-range-value {
      .react-calendar__navigation__label {
        font-size: 13px;
      }
      .date-value-button {
        width: 110px !important;
        font-size: 12px;
      }
    }
    .react-calendar__month-view {
      .react-calendar__month-view__days {
        gap: 0 0px;
      }
      .date-range-picker .react-calendar__navigation__arrow {
        min-width: 34px !important;
        min-height: 30px !important;
      }
      .react-calendar__tile {
        width: 34px !important;
        height: 34px !important;
        padding: 0;
        /* flex: unset !important; */
      }
    }
    .Footer {
      justify-content: center;
      gap: 0px 25px;
      .footer-buttons {
        width: 100px;
        height: 30px;
      }
    }
  }
  @media (min-width: 1537px) {
    .calendar-open {
      opacity: 1;
      /* transform: translate(-50%, -50%);  */
    }
  }
`;
const CustomButton = styled.div`
  width: 100%;
  min-width: 165px;
  max-width: 280px;
  height: 26px;
  background: ${p => p.theme.customDateRangeMainBg};
  border: 1px solid ${p => p.theme.customDateRangeMainBorder};
  border-radius: 4px;
  font-family: 'Open Sans';
  font-size: 11px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  padding: 1px 16px;
  @media (min-width: 320px) and (max-width: 767px) {
    height: 40px;
    padding: 1px 10px !important;
    .icon-wrapper {
      height: 40px;
      .btn-content {
        margin: 0 10px 0 0 !important;
      }
    }
  }
  cursor: pointer;
  position: relative;
  user-select: none;
  &.opened {
    .icon-wrapper {
      svg {
        transform: rotate(180deg); /* Rotates the element */
      }
    }
  }
  .icon-wrapper {
    display: flex;
    align-items: center;
    .btn-content {
      margin: 0 15px 0 0;
    }
    svg {
      transition: transform 0.5s ease-in-out; /* Smooth transition */
      margin: 0 0 0 auto;
    }
  }
  &.selected {
    min-width: 178px;
    background: ${p => p.theme.customDateRangeMainSelectedBg};
    .icon-wrapper {
      color: ${p => p.theme.customDateRangeMainSelectedColor};
      font-family: Open Sans;
      font-size: 11px;
      font-weight: 700;
      line-height: 24px;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
    }
  }
`;
const Footer = styled.div`
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  background: ${p => p.theme.customDateRangeSelectorBg};
  color: ${p => p.theme.customDateRangeFooterColor};
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 70px;
  font-family: Inter;
  font-size: 11px;
  font-weight: 400;
  line-height: 16.5px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  .footer-buttons {
    border: 1px solid ${p => p.theme.customDateRangeSelectorButtonBrdrClr};
    height: 40px;
    width: 142px;
    border-radius: 4px;
    button {
      width: 100%;
      font-size: 14px;
      font-weight: 600;
      height: 40px;
    }
  }
  .cancel-button {
    border-color: ${p => p.theme.customDateRangeCancelBtnBorderClr};
    color: ${p => p.theme.customDateRangeCancelBtnClr};
    background: transparent !important;
    cursor: pointer;
    &:hover {
      background: ${p => p.theme.exportBtnHoverBg} !important;
      color: ${p => p.theme.white};
    }
  }
  .apply-button {
    background: ${p => p.theme.customDateRangeSelectorButtonSecndBgClr};
    color: ${p => p.theme.customDateRangeSelectorButtonSecndClr};
    border: none;
    &:hover:not(.disabled) {
      background: ${p => p.theme.discardBgHvrClr};
      cursor: pointer;
      color: ${p => p.theme.white};
    }
    &:disabled {
      &:hover {
        background: ${p => p.theme.customDateRangeSelectorButtonSecndBgClr};
      }
    }
  }
  .disabled {
    cursor: not-allowed;
  }
`;
const CalendarWrapper = styled.div`
  position: absolute;
  left: 0px;
  z-index: 5;
  margin-top: 5px;
  width: 328px;
  border: 1px solid ${p => p.theme.customDateRangeCalenderBorder};
  border-radius: 4px;
  opacity: 0;
  .date-range-value {
    position: absolute;
    left: 28px;
    top: 80px;
    display: flex;
    font-size: 16px;
    font-family: 'Open Sans';
    .in-between {
      color: ${p => p.theme.customDateRangeSelectorHyphenClr};
      font-size: 16px;
      display: flex;
      align-items: center;
      margin: 0 4px;
    }
    .date-value-button {
      border: 1px solid ${p => p.theme.customDateRangeDateValueBorder};
      background: ${p => p.theme.customDateRangeDateValueBg};
      border-radius: 5px;
      height: 30px;
      width: 128px;
      color: ${p => p.theme.customDateRangeDateValueColor};
      font-family: Open Sans;
      font-size: 12px;
      font-weight: 400;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
  .react-calendar {
    border: none;
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
    background: ${p => p.theme.customDateRangeSelectorBg};
  }
  .date-range-picker {
    padding-top: 22px;
    .react-calendar__viewContainer {
      padding-bottom: 16px;
    }
    .react-calendar__year-view {
      .react-calendar__tile--hasActive {
        background: none !important;
        &::before {
          content: '';
          position: absolute;
          background: #3085fe;
          z-index: -1;
          height: 5px;
          width: 5px;
          border-radius: 50%;
          bottom: 3px;
          left: 18px;
        }
      }
    }
    .react-calendar__navigation__arrow {
      color: ${p => p.theme.customDateRangeSelectorHyphenClr};
      border: 1px solid ${p => p.theme.customDateRangeNavBorder};
      border-radius: 3px;
      font-size: 30px;
      min-width: 32px;
      max-width: 32px;
      min-height: 32px;
      max-height: 32px;
      padding: 0 0 0 0 !important;
      display: flex;
      align-items: center;
      justify-content: center;
      &:focus {
        background: none;
      }
      &:hover {
        background: ${p => p.theme.customDateRangeSelectorDaysHvr};
      }
    }
    .react-calendar__navigation__label {
      color: ${p => p.theme.customDateRangeNavTextColor};
      font-family: 'Open Sans';
      font-size: 11px;
      font-weight: 400;
      line-height: 24px;
      text-align: center;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
      padding-top: 2px;
      border-radius: 4px;
      margin: 0 10px;
      &:hover {
        background: ${p => p.theme.customDateRangeSelectorDaysHvr};
      }
      &:focus {
        background-color: unset !important;
      }
    }
    .react-calendar__navigation {
      margin-bottom: 5em;
      margin-left: 18px;
      margin-right: 18px;
      display: flex;
      align-items: center;
    }
    .react-calendar__navigation__prev2-button {
      display: none;
    }
    .react-calendar__navigation__next2-button {
      display: none;
    }
    .react-calendar__tile--now {
      background: none;
      &::before {
        content: '';
        position: absolute;
        background: ${p => p.theme.customDateRangeSelectorTodayDot};
        z-index: -1;
        height: 5px;
        width: 5px;
        border-radius: 50%;
        bottom: 3px;
        left: 18px;
      }
      &:hover {
        background: ${p => p.theme.customDateRangeSelectorDaysHvr};
      }
    }
    .react-calendar__month-view__weekdays {
      text-transform: capitalize;
      font-family: Open Sans;
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
      text-align: center;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
    }
    .react-calendar__month-view__weekdays__weekday {
      color: ${p => p.theme.customDateRangeSelectorWeekDays};
      abbr {
        text-decoration: unset;
        text-decoration: unset;
        white-space: nowrap;
        display: flex;
        overflow: hidden;
        margin-left: 4px;
      }
    }
    .react-calendar__month-view__weekdays__weekday {
      abbr[aria-label='Monday'] {
        width: 25px;
      }
      abbr[aria-label='Tuesday'] {
        width: 25px;
      }
      abbr[aria-label='Wednesday'] {
        width: 25px;
      }
      abbr[aria-label='Thursday'] {
        width: 25px;
      }
      abbr[aria-label='Friday'] {
        width: 25px;
      }
      abbr[aria-label='Saturday'] {
        width: 25px;
      }
      abbr[aria-label='Sunday'] {
        width: 25px;
      }
    }
  }

  .no-date-selected {
    .react-calendar__navigation {
      margin-bottom: 14px;
    }
  }
  .react-calendar__tile--active {
    background: ${p => p.theme.customDateRangeSelectorActiveTileBg} !important;
    color: ${p => p.theme.customDateRangeSelectorTileActiveClr} !important;
    &.sunday {
      border-bottom-right-radius: 26px;
      border-top-right-radius: 26px;
    }
    &.monday {
      border-bottom-left-radius: 26px;
      border-top-left-radius: 26px;
    }

    &:hover {
      border-radius: 0px !important;
      background: ${p => p.theme.customDateRangeSelectorActiveTileBg};
    }
    &:focus {
      border-radius: 0px !important;
      background: ${p => p.theme.customDateRangeSelectorActiveTileBg};
    }
  }
  .react-calendar__month-view {
    .react-calendar__tile--rangeEnd {
      color: ${p => p.theme.customDateRangeSelectorTileFocusedClr} !important;
      background: linear-gradient(
        90deg,
        rgba(204, 204, 204, 1) 0%,
        rgba(255, 255, 255, 1) 100%
      ) !important;
      &::before {
        content: '' !important;
        position: absolute;
        background: ${p =>
          p.theme.customDateRangeSelectorButtonSecndBgClr} !important;
        z-index: -1 !important;
        height: 100% !important;
        width: 100% !important;
        left: 0 !important;
        top: 0 !important;
        border-radius: 50% !important;
      }
      &.sunday {
        border-bottom-right-radius: 0px;
        border-top-right-radius: 0px;
      }
      &.monday {
        border-bottom-left-radius: 0px;
        border-top-left-radius: 0px;
      }
    }
  }
  .react-calendar__month-view {
    .react-calendar__tile--rangeStart {
      color: ${p => p.theme.customDateRangeSelectorTileFocusedClr} !important;
      &.react-calendar__tile--hover {
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 1) 0%,
          rgba(204, 204, 204, 1) 100%
        ) !important;
      }
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 1) 0%,
        rgba(204, 204, 204, 1) 100%
      ) !important;
      &::before {
        content: '' !important;
        position: absolute !important;
        background: ${p =>
          p.theme.customDateRangeSelectorButtonSecndBgClr} !important;
        z-index: -1 !important;
        height: 100% !important;
        width: 100% !important;
        left: 0 !important;
        top: 0 !important;
        border-radius: 50% !important;
      }
      &.sunday {
        border-bottom-right-radius: 0px;
        border-top-right-radius: 0px;
      }
      &.monday {
        border-bottom-left-radius: 0px;
        border-top-left-radius: 0px;
      }
    }
  }
  .react-calendar__tile {
    color: ${p => p.theme.customDateRangeSelectorDays};
    position: relative;
    z-index: 1;
    margin: 2px 0;
    font-family: 'Open Sans';
    font-size: 11px;
    font-weight: 400;
    line-height: 16.5px;
    text-align: center;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;

    abbr {
      z-index: 2;
    }
    &:hover {
      border-radius: 18px;
      background: ${p => p.theme.customDateRangeSelectorDaysHvr};
    }
  }
  .react-calendar__month-view__days__day--neighboringMonth {
    color: ${p => p.theme.customDateRangeSelectorNeighboringMonthDays};
  }
  .react-calendar__month-view {
    margin: 0 20px;
  }
  .react-calendar__tile--hover {
    background: ${p => p.theme.customDateRangeSelectorActiveTileBg} !important;
    &.sunday {
      border-bottom-right-radius: 26px;
      border-top-right-radius: 26px;
    }
    &.monday {
      border-bottom-left-radius: 26px;
      border-top-left-radius: 26px;
    }
  }
`;
const DateRangeWrapper = styled.div`
  border: none;
  position: relative;
`;
